import type { Address, CompanyLookup } from '@/shared/api/resources';
import {
  digitosIguais,
  dinheiroIgual,
  dinheiroParaCampo,
  enderecoEmTexto,
  enderecoVazio,
  mesmoEndereco,
  type CampoDaReceita,
} from '@/shared/lib/consultaCnpj';
import { formatCivilDate, formatCnae } from '@/shared/lib/format';

import { situacoesNaReceita } from './rotulos';

/**
 * De onde vem cada campo do cadastro do tomador na consulta de CNPJ (ADR 0092).
 *
 * A Situacao do formulario e a do tomador na plataforma (ativo, inativo, bloqueado) e
 * nao e tocada: a situacao cadastral da Receita vai para "Cadastro na Receita". Contato
 * e inscricao estadual nao vem da consulta: o quadro societario tem nome de pessoa e
 * nao e lido, e a BrasilAPI nao traz a inscricao.
 */
export type CampoDoTomador =
  | 'legal_name'
  | 'trade_name'
  | 'registration_situation'
  | 'legal_nature'
  | 'cnae'
  | 'secondary_cnaes'
  | 'share_capital'
  | 'company_size'
  | 'founded_on'
  | 'email'
  | 'phone'
  | 'address';

/** O que o formulario tem agora, nos campos que a consulta preenche. */
export interface ValoresDoTomador {
  legal_name: string;
  trade_name: string;
  registration_situation: string;
  legal_nature: string;
  cnae: string;
  cnae_description: string;
  secondary_cnaes: string[];
  /** O texto do campo de dinheiro, como digitado. */
  share_capital: string;
  company_size: string;
  founded_on: string;
  email: string;
  phone: string;
  address: Address;
}

const rotulosDoPorte: Record<string, string> = {
  mei: 'MEI',
  me: 'Microempresa',
  epp: 'Empresa de pequeno porte',
  medio: 'Medio porte',
  grande: 'Grande porte',
};

function rotuloDaSituacao(valor: string): string {
  return situacoesNaReceita.find((item) => item.value === valor)?.label ?? valor;
}

function cnaeEmTexto(codigo: string, descricao: string): string {
  if (!codigo) return '';
  return descricao ? `${formatCnae(codigo)} - ${descricao}` : formatCnae(codigo);
}

/**
 * Os campos do tomador com o valor do formulario e o da Receita.
 *
 * `comCadastroNaReceita` e falso no portal da corretora, onde o campo "Cadastro na
 * Receita" nao aparece.
 */
export function camposDoTomadorDaReceita(
  atual: ValoresDoTomador,
  r: CompanyLookup,
  { comCadastroNaReceita }: { comCadastroNaReceita: boolean },
): CampoDaReceita<CampoDoTomador>[] {
  const campos: CampoDaReceita<CampoDoTomador>[] = [
    {
      chave: 'legal_name',
      rotulo: 'Razao social',
      atual: atual.legal_name,
      receita: r.legal_name,
    },
    {
      chave: 'trade_name',
      rotulo: 'Nome fantasia',
      atual: atual.trade_name,
      receita: r.trade_name,
    },
  ];
  if (comCadastroNaReceita) {
    campos.push({
      chave: 'registration_situation',
      rotulo: 'Cadastro na Receita',
      atual: rotuloDaSituacao(atual.registration_situation),
      receita: rotuloDaSituacao(r.registration_situation),
      igual: () => atual.registration_situation === r.registration_situation,
    });
  }
  campos.push(
    {
      chave: 'legal_nature',
      rotulo: 'Natureza juridica',
      atual: atual.legal_nature,
      receita: r.legal_nature,
    },
    {
      chave: 'cnae',
      rotulo: 'Atividade principal (CNAE)',
      atual: cnaeEmTexto(atual.cnae, atual.cnae_description),
      receita: cnaeEmTexto(r.cnae, r.cnae_description),
      igual: () => digitosIguais(atual.cnae, r.cnae),
    },
    {
      chave: 'secondary_cnaes',
      rotulo: 'CNAEs secundarios',
      atual: atual.secondary_cnaes.map(formatCnae).join(', '),
      receita: r.secondary_cnaes.map((item) => formatCnae(item.code)).join(', '),
      igual: () =>
        [...atual.secondary_cnaes].sort().join() ===
        r.secondary_cnaes
          .map((item) => item.code)
          .sort()
          .join(),
    },
    {
      chave: 'share_capital',
      rotulo: 'Capital social (R$)',
      atual: atual.share_capital,
      receita: dinheiroParaCampo(r.share_capital),
      igual: dinheiroIgual,
    },
    {
      chave: 'company_size',
      rotulo: 'Porte',
      atual: rotulosDoPorte[atual.company_size] ?? atual.company_size,
      receita: rotulosDoPorte[r.company_size] ?? r.company_size,
      igual: () => atual.company_size === r.company_size,
    },
    {
      chave: 'founded_on',
      rotulo: 'Data de abertura',
      atual: formatCivilDate(atual.founded_on),
      receita: formatCivilDate(r.founded_on),
      igual: () => atual.founded_on === (r.founded_on ?? ''),
    },
    { chave: 'email', rotulo: 'Email', atual: atual.email, receita: r.email },
    {
      chave: 'phone',
      rotulo: 'Telefone',
      atual: atual.phone,
      receita: r.phone,
      igual: digitosIguais,
    },
    {
      chave: 'address',
      rotulo: 'Endereco',
      atual: enderecoVazio(atual.address) ? '' : enderecoEmTexto(atual.address),
      receita: enderecoEmTexto(r.address),
      igual: () => mesmoEndereco(atual.address, r.address),
    },
  );
  return campos;
}
